import pMap from 'p-map';

/**
 * Result type for promise handling with discriminated union:
 * Either [data, null] or [null, error]
 */
type PromiseResult<T> =
  | [data: T, error: undefined]
  | [data: undefined, error: unknown];

/**
 * Attempts to execute the given promise and returns its result and potential errors.
 *
 * @param promise - The promise to handle.
 * @returns A promise that resolves to a tuple where the first element is the resolved data or undefined, and the second element is the error or undefined.
 */
export async function handlePromise<T>(
  promise: Promise<T>,
): Promise<PromiseResult<T>> {
  try {
    const data = await promise;

    return [data, undefined];
  } catch (error) {
    return [undefined, error];
  }
}

/**
 * Maximum number of concurrent requests.
 * Helpful for throttling requests to avoid '429 Too Many Requests' errors.
 * Reduced from 6 to 3 to prevent API rate limiting issues.
 * @see VGS-9582
 */
export const MAX_CONCURRENT_REQUESTS = 3;

/**
 * Executes an array of promises with a concurrency limit and returns their settled results.
 * Similar to Promise.allSettled() but with concurrency control.
 *
 * @param promises - An array of promises to be executed
 * @param concurrency - Maximum number of promises to execute simultaneously (defaults to MAX_CONCURRENT_REQUESTS)
 * @returns An array of PromiseSettledResult objects containing the status ('fulfilled' or 'rejected') and value/reason
 *
 * @example
 * const promises = [fetch(url1), fetch(url2), fetch(url3)];
 * const results = await promiseAllSettledThrottled(promises, 2);
 */
export const promiseAllSettledThrottled = async <T>(
  promises: Array<Promise<T>>,
  concurrency: number = MAX_CONCURRENT_REQUESTS,
): Promise<Array<PromiseSettledResult<T>>> => {
  if (!promises?.length) {
    return [];
  }

  try {
    const results = await pMap(
      promises,
      async (promise: Promise<T>) => {
        try {
          return await promise;
        } catch (error) {
          return Promise.reject(error);
        }
      },
      { concurrency },
    );

    return results.map((result) => ({
      status: 'fulfilled',
      value: result,
    }));
  } catch (error) {
    return [
      {
        reason: error,
        status: 'rejected',
      },
    ];
  }
};

/**
 * Executes an array of promises with a concurrency limit.
 * Similar to Promise.all() but with concurrency control.
 * If any promise rejects, the entire operation rejects.
 *
 * @param promises - An array of promises to be executed
 * @param concurrency - Maximum number of promises to execute simultaneously (defaults to MAX_CONCURRENT_REQUESTS)
 * @returns An array of resolved values from the promises
 * @throws Will throw an error if any of the promises reject
 *
 * @example
 * const promises = [fetch(url1), fetch(url2), fetch(url3)];
 * const results = await promiseAllThrottled(promises, 2);
 */
export const promiseAllThrottled = async <T>(
  promises: Array<Promise<T>>,
  concurrency: number = MAX_CONCURRENT_REQUESTS,
): Promise<T[]> => {
  if (!promises?.length) {
    return [];
  }

  const results = await pMap(
    promises,
    async (promise: Promise<T>) => {
      return await promise;
    },
    { concurrency },
  );

  return results;
};
